import React, { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";
import { trackEvent } from "@/lib/analytics";
import { GuideCta } from "@/components/GuideCta";
import { HOME } from "@/lib/homeContent";
import { useSiteContent } from "@/contexts/SiteContentContext";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

const split = (s, sep) => (s || "").split(sep).map((x) => x.trim()).filter(Boolean);

// Centered editorial dialog for one How We Help step. Radix Dialog handles Escape, focus trap and focus return.
export default function HowWeHelpDeepDive({ open, onOpenChange, step, index, profile, c, returnFocusTo }) {
  const route = profile || "neutral";
  const stepId = step?.id || "";
  const source = `how_we_help_${route}_${stepId}`;

  const handoff = useRef(false);
  const { section } = useSiteContent();
  const ctas = section("conversion_ctas", HOME.conversion_ctas);

  const onCall = () => {
    trackEvent("how_we_help_cta_click", { profile: route, step_id: stepId, cta_type: "call", placement: "deep_dive" });
    handoff.current = true;
    onOpenChange(false);
  };

  const onGuide = () => {
    trackEvent("how_we_help_cta_click", { profile: route, step_id: stepId, cta_type: "guide", placement: "deep_dive" });
    handoff.current = true;
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="how-we-help-deep-dive"
        data-route={route}
        data-step={stepId}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (!handoff.current) returnFocusTo?.current?.focus({ preventScroll: true });
          handoff.current = false;
        }}
        className="z-[80] w-[calc(100%-2rem)] max-w-[860px] max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-y-auto gap-0 p-0 rounded-none sm:rounded-none border-0 bg-[#F5EFE7] text-[#451C25] shadow-2xl motion-reduce:animate-none [&>button]:text-[#451C25] [&>button]:opacity-80 [&>button]:z-10 [&>button]:bg-[#F5EFE7]/90 [&>button]:p-1.5 [&>button]:right-4 [&>button]:top-4 [&>button]:focus:ring-[#B3A17D]"
      >
        {step && (
          <article className="flex flex-col">
            <div className="relative h-52 sm:h-72 md:h-80 shrink-0">
              <img src={step.image} alt={step.alt || ""} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#F5EFE7] via-transparent to-transparent" />
            </div>
            <div className="px-6 sm:px-12 md:px-16 pb-10 md:pb-14 -mt-8 relative">
              <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase font-semibold" style={K}>
                Step 0{index + 1} · {step.title}
              </p>
              <DialogTitle data-testid="deep-dive-title" className="mt-3 text-3xl sm:text-4xl md:text-[44px] leading-[1.05] tracking-tight text-[#451C25] max-w-2xl" style={{ ...K, fontWeight: 600 }}>
                {step.deep_title}
              </DialogTitle>
              <DialogDescription className="sr-only">{step.short}</DialogDescription>

              <div data-testid="deep-dive-body" className="mt-6 space-y-4 text-[#6B4954] text-[15px] sm:text-base md:text-[17px] leading-relaxed max-w-2xl" style={S}>
                {split(step.deep_body, /\n\s*\n/).map((para, i) => <p key={i}>{para}</p>)}
              </div>

              {step.deep_points && (
                <div className="mt-8">
                  <p className="text-[#897756] text-[11px] tracking-[0.26em] uppercase font-semibold" style={K}>{c.points_label}</p>
                  <ul data-testid="deep-dive-points" className="mt-3 border-t border-[#451C25]/15">
                    {split(step.deep_points, "|").map((pt, i) => (
                      <li key={i} className="py-3 border-b border-[#451C25]/15 text-[15px] leading-relaxed grid grid-cols-[20px_1fr] gap-3">
                        <span className="text-[#B3A17D]" style={K}>—</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {step.deep_help && (
                <div className="mt-8 border-l-2 border-[#B3A17D] pl-5">
                  <p className="text-[#897756] text-[11px] tracking-[0.26em] uppercase font-semibold" style={K}>{c.help_label}</p>
                  <p data-testid="deep-dive-help" className="mt-2 text-[#451C25] text-[15px] sm:text-base leading-relaxed" style={S}>{step.deep_help}</p>
                </div>
              )}

              <div data-testid="deep-dive-cta" className="mt-10 pt-8 border-t border-[#451C25]/15">
                <p className="text-xl sm:text-2xl leading-tight" style={{ ...K, fontWeight: 500 }}>{c[`${route}_cta_question`]}</p>
                <div className="mt-5">
                  <ScheduleCallButton tone="burgundy" label={c[`${route}_cta_label`]} source={source} testId="deep-dive-cta-call" onClick={onCall} className="w-full sm:w-auto" />
                </div>
                <p className="mt-3 text-[#6B4954] text-[12px] tracking-[0.12em] uppercase" style={K}>{ctas.call_meta}</p>
                <GuideCta placement={`deep_dive_${route}_${stepId}`} variant="compact" className="mt-6" onBefore={onGuide} testId="deep-dive-cta-guide" />
              </div>
            </div>
          </article>
        )}
      </DialogContent>
    </Dialog>
  );
}
